import { useState, useRef, useEffect } from "react";
import { format } from "date-fns";
import { toPng } from "html-to-image";
import PropTypes from "prop-types";
import { Download } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Replace the existing font-face definition at the top
const fontFaceStyle = `
  @font-face {
    font-family: 'Degular Bold';
    src: url('/fonts/degular.ttf') format('truetype');
  }
`;

export function HaafReceiptModal({ isOpen, onClose, payment }) {
  const receiptRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrency, setShowCurrency] = useState(false);

  // Update the font loading function
  const loadCustomFont = async () => {
    const customFont = new FontFace("Degular Bold", "url(/fonts/degular.ttf)");

    try {
      const loadedFont = await customFont.load();
      document.fonts.add(loadedFont);
    } catch (error) {
      console.error("Failed to load Degular Bold font:", error);
    }
  };

  // Load font when component mounts
  useEffect(() => {
    loadCustomFont();
  }, []);

  if (!payment) return null;

  // Helper function to render receipt content
  const renderReceiptContent = (withCurrency = false) => {
    const backgroundImage = withCurrency
      ? "url('/haf-currency-receipt.jpg')"
      : "url('/haf-receipt.jpg')";

    return (
      <div
        style={{
          width: "504px",
          height: "630px",
          backgroundImage,
          backgroundSize: "100% 100%",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          fontFamily: "'Degular Bold', sans-serif",
          position: "relative",
        }}
      >
        {/* Payment ID - positioned at "No." field */}
        <div
          style={{
            position: "absolute",
            left: "116px",
            top: "338px",
            fontSize: "12px",
            color: "#BA0015",
            fontWeight: "600",
          }}
        >
          {payment.paymentId}
        </div>

        {/* Date - positioned at "Date:" field */}
        <div
          style={{
            position: "absolute",
            left: "336px",
            top: "338px",
            fontSize: "12px",
            color: "#000",
            fontWeight: "700",
          }}
        >
          {format(new Date(payment.createdAt), "dd-MM-yyyy")}
        </div>

        {/* Name - positioned after "Mr." */}
        <div
          style={{
            position: "absolute",
            left: "123px",
            top: "364px",
            width: "282px",
            fontSize: "12px",
            fontWeight: "600",
            color: "#BA0015",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
          }}
        >
          {payment.payee}
        </div>

        {/* Chapter - positioned in the acknowledgment text area */}
        <div
          style={{
            position: "absolute",
            left: "154px",
            top: "379px",
            width: "400px",
            fontSize: "12px",
            color: "#000",
            letterSpacing: "0.5px",
            fontWeight: "600",
          }}
        >
          {payment.chapter?.chapterName || "—"}
        </div>

        {/* BATCH - positioned in the acknowledgment text area */}
        <div
          style={{
            position: "absolute",
            left: "320px",
            top: "379px",
            width: "400px",
            fontSize: "12px",
            color: "#000",
            letterSpacing: "0.5px",
            fontWeight: "600",
          }}
        >
          {payment.batch}
        </div>

        {/* Currency - positioned before amount (only for currency design) */}
        {withCurrency && (
          <div
            style={{
              position: "absolute",
              left: "244px",
              top: "241px",
              fontSize: "24px",
              color: "#9D6704",
              fontWeight: "700",
            }}
          >
            {payment.currency}
          </div>
        )}

        {/* Amount - positioned in the main content area */}
        <div
          style={{
            position: "absolute",
            left: "284px",
            top: "241px",
            fontSize: "18px",
            color: "#1A1914",
            fontWeight: "900",
          }}
        >
          <span
            style={{ color: "#C02314", fontWeight: "bold", fontSize: "24px" }}
          >
            {payment.amount.toLocaleString("en-IN")}/-
          </span>
        </div>
      </div>
    );
  };

  const handleDownload = async () => {
    try {
      setIsLoading(true);
      await document.fonts.ready;

      // Create a temporary div for generating the receipt
      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.left = "-9999px";
      tempDiv.style.top = "-9999px";
      document.body.appendChild(tempDiv);

      // Render the receipt content in the temporary div
      const receiptContent = renderReceiptContent(showCurrency);

      // Use createRoot instead of ReactDOM.render (modern approach)
      const { createRoot } = await import("react-dom/client");
      const root = createRoot(tempDiv);
      root.render(receiptContent);

      // Wait for images to load
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Generate the image
      const options = {
        width: 504 * 2,
        height: 630 * 2,
        style: {
          transform: "scale(2)",
          transformOrigin: "top left",
          width: "504px",
          height: "630px",
        },
        quality: 0.95,
        pixelRatio: 2,
      };

      const dataUrl = await toPng(tempDiv.firstChild, options);

      // Clean up - use unmount instead of unmountComponentAtNode
      root.unmount();
      document.body.removeChild(tempDiv);

      // Detect iOS
      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

      if (isIOS) {
        // For iOS: Open image in new tab for manual saving
        const newTab = window.open();
        if (newTab) {
          newTab.document.write(`
            <html>
              <head><title>HAF Payment Receipt</title></head>
              <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f5f5f5;">
                <img src="${dataUrl}" style="max-width:100%;height:auto;" alt="Receipt" />
                <p style="position:absolute;bottom:20px;text-align:center;width:100%;color:#666;">
                  Long press the image and select "Save to Photos" to download
                </p>
              </body>
            </html>
          `);
          newTab.document.close();
        } else {
          alert("Please allow popups to download the receipt");
        }
      } else {
        // For non-iOS devices: Use the standard download approach
        const link = document.createElement("a");
        link.download = `haf-payment-receipt-${payment.paymentId}.png`;
        link.href = dataUrl;
        link.click();
      }
    } catch (error) {
      console.error("Error generating receipt:", error);
      alert(
        "An error occurred while generating the receipt. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{fontFaceStyle}</style>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          className="sm:max-w-[95vw] max-h-[95vh] overflow-y-auto p-4 md:p-6"
          onInteractOutside={(e) => e.preventDefault()}
        >
          {/* Mobile View */}
          <div className="block sm:hidden">
            <div className="flex flex-col items-center space-y-4">
              <div className="text-center space-y-2">
                <h3 className="font-semibold text-lg">HAF Payment Receipt</h3>
                <p className="text-sm text-muted-foreground">
                  Download receipt for HAF payment of {payment.currency}{" "}
                  {payment.amount}
                </p>
              </div>

              {/* Toggle Switch for Receipt Design */}
              <div className="flex items-center space-x-2 text-xs">
                <span className="font-medium">Standard</span>
                <button
                  onClick={() => setShowCurrency(!showCurrency)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    showCurrency ? "bg-blue-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      showCurrency ? "translate-x-5" : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="font-medium">Currency</span>
              </div>

              <div className="w-full max-w-sm space-y-4">
                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Date</span>
                    <span className="text-sm font-medium">
                      {format(new Date(payment.createdAt), "dd/MM/yyyy")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Name</span>
                    <span className="text-sm font-medium">{payment.payee}</span>
                  </div>
                  {payment.phoneNumber && (
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">
                        Phone
                      </span>
                      <span className="text-sm font-medium">
                        {payment.phoneNumber}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      Chapter
                    </span>
                    <span className="text-sm font-medium">
                      {payment.chapter?.chapterName || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Batch</span>
                    <span className="text-sm font-medium">{payment.batch}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      Amount
                    </span>
                    <span className="text-sm font-medium">
                      {payment.currency}{" "}
                      {payment.amount.toLocaleString("en-IN")}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={handleDownload}
                  className="w-full"
                  disabled={isLoading}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {isLoading ? "Generating..." : "Download Receipt"}
                </Button>
              </div>
            </div>
          </div>

          {/* Desktop View */}
          <div className="hidden sm:block">
            <div className="flex flex-col items-center space-y-4 w-full">
              {/* Toggle Switch for Receipt Design */}
              <div className="flex items-center space-x-3 mb-4">
                <span className="text-sm font-medium">Standard Receipt</span>
                <button
                  onClick={() => setShowCurrency(!showCurrency)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    showCurrency ? "bg-blue-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showCurrency ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-sm font-medium">Currency Receipt</span>
              </div>

              <div
                className="relative w-[504px] h-[630px]"
                style={{
                  maxWidth: "100%",
                  aspectRatio: "504/630",
                }}
              >
                <div
                  ref={receiptRef}
                  className="absolute inset-0 bg-white"
                  style={{
                    backgroundImage: showCurrency
                      ? "url('/haf-currency-receipt.jpg')"
                      : "url('/haf-receipt.jpg')",
                    backgroundSize: "100% 100%",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    width: "504px",
                    height: "630px",
                    fontFamily: "'Degular Bold', sans-serif",
                  }}
                >
                  {/* Payment ID - positioned at "No." field */}
                  <div
                    className="absolute left-[116px] top-[338px]"
                    style={{
                      fontSize: "12px",
                      color: "#BA0015",
                      fontWeight: "600",
                    }}
                  >
                    {payment.paymentId}
                  </div>

                  {/* Date - positioned at "Date:" field */}
                  <div
                    className="absolute left-[336px] top-[338px]"
                    style={{
                      fontSize: "12px",
                      color: "#000",
                      fontWeight: "700",
                    }}
                  >
                    {format(new Date(payment.createdAt), "dd-MM-yyyy")}
                  </div>

                  {/* Name - positioned after "Mr." */}
                  <div
                    className="absolute left-[123px] top-[364px] w-[282px]"
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#BA0015",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    {payment.payee}
                  </div>

                  {/* Chapter - positioned in the acknowledgment text area */}
                  <div
                    className="absolute left-[154px] top-[379px] w-[400px]"
                    style={{
                      fontSize: "12px",
                      color: "#000",
                      letterSpacing: "0.5px",
                      fontWeight: "600",
                    }}
                  >
                    {payment.chapter?.chapterName || "—"}
                  </div>

                  {/* BATCH - positioned in the acknowledgment text area */}
                  <div
                    className="absolute left-[320px] top-[379px] w-[400px]"
                    style={{
                      fontSize: "12px",
                      color: "#000",
                      letterSpacing: "0.5px",
                      fontWeight: "600",
                    }}
                  >
                    {payment.batch}
                  </div>

                  {/* Currency - positioned before amount (only for currency design) */}
                  {showCurrency && (
                    <div
                      className="absolute left-[244px] top-[241px]"
                      style={{
                        fontSize: "24px",
                        color: "#9D6704",
                        fontWeight: "700",
                      }}
                    >
                      {payment.currency}
                    </div>
                  )}

                  {/* Amount - positioned in the main content area */}
                  <div
                    className="absolute left-[284px] top-[241px] font-bold"
                    style={{
                      fontSize: "18px",
                      color: "#1A1914",
                      fontWeight: "900",
                    }}
                  >
                    <span
                      style={{
                        color: "#C02314",
                        fontWeight: "bold",
                        fontSize: "24px",
                      }}
                    >
                      {payment.amount.toLocaleString("en-IN")}/-
                    </span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleDownload}
                className="mt-4"
                disabled={isLoading}
              >
                <Download className="mr-2 h-4 w-4" />
                {isLoading ? "Generating..." : "Download Receipt"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

HaafReceiptModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  payment: PropTypes.shape({
    paymentId: PropTypes.string.isRequired,
    payee: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string,
    chapter: PropTypes.shape({
      chapterName: PropTypes.string,
    }),
    batch: PropTypes.string.isRequired,
    amount: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
  }),
};
